import {useMemo} from 'react';
import {Node, OnNodesChange} from 'reactflow';

import {assert} from 'Asserts';
import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {BaseCondition} from 'studies/typed/shared/useExperimentDesignState';

import {ProcedureRunDesignFlowchartColumnNodeData} from './ProcedureRunDesignFlowchartColumnNode';
import {GridLayoutOptions, makeInitialNodes, StatusByNodeId} from './ProcedureRunDesignFlowchartLayoutUtils';
import {ProcedureRunDesignFlowchartNodeData} from './ProcedureRunDesignFlowchartNode';
import type {MethodHighlightState} from './useProcedureRunDesignFlowchartHighlighting';
import {useProcedureRunDesignFlowchartHighlighting} from './useProcedureRunDesignFlowchartHighlighting';
import {useProcedureRunDesignFlowchartMeasuredLayout} from './useProcedureRunDesignFlowchartMeasuredLayout';
import {useProcedureRunDesignFlowchartNodes} from './useProcedureRunDesignFlowchartNodes';

export interface ProcedureRunDesignFlowchartLayoutOptions extends GridLayoutOptions {
  columnHeaderBottomGap: number;
}

export type ProcedureRunDesignFlowchartLayoutNodeData =
  | ProcedureRunDesignFlowchartNodeData
  | ProcedureRunDesignFlowchartColumnNodeData;
export type ProcedureRunDesignFlowchartLayoutNode = Node<ProcedureRunDesignFlowchartLayoutNodeData>;

export interface UseProcedureRunDesignFlowchartLayoutArgs {
  conditions: Array<BaseCondition>;
  methods: Array<ExperimentMethod>;
  layoutOptions: ProcedureRunDesignFlowchartLayoutOptions;
  methodHighlightState: MethodHighlightState;
  statusByNodeId?: StatusByNodeId;
}

export interface UseProcedureRunDesignFlowchartLayoutResult {
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  onNodesChange: OnNodesChange;
}

/**
 * Manages the two-phase layout for ProcedureRunDesignFlowchart:
 * 1. Create hidden placeholder nodes so ReactFlow can measure their dimensions.
 * 2. Once dimensions are available, compute grid positions and unhide nodes.
 */
export function useProcedureRunDesignFlowchartLayout({
  conditions,
  methods,
  layoutOptions,
  methodHighlightState,
  statusByNodeId,
}: UseProcedureRunDesignFlowchartLayoutArgs): UseProcedureRunDesignFlowchartLayoutResult {
  // A procedure run design always has at least one condition
  assert(conditions.length >= 1, 'Expected at least one condition');

  const {columnNodes: initialColumnNodes, rows: initialRows} = useMemo(
    () => makeInitialNodes(conditions, methods, statusByNodeId),
    [conditions, methods, statusByNodeId]
  );

  const {nodes, setNodes, onNodesChange} = useProcedureRunDesignFlowchartNodes({
    initialColumnNodes,
    initialRows,
  });

  useProcedureRunDesignFlowchartHighlighting({
    methodHighlightState,
    initialColumnNodes,
    initialRows,
    setNodes,
  });

  useProcedureRunDesignFlowchartMeasuredLayout({
    initialColumnNodes,
    initialRows,
    layoutOptions,
    nodes,
    setNodes,
  });

  return {nodes, onNodesChange};
}
